import { Component, ElementRef, Input, ViewChild, AfterViewInit, OnDestroy, HostListener, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'vw-calendar-month-line',
    imports: [CommonModule],
    templateUrl: './calendar-month-line.component.html',
    styleUrl: './calendar-month-line.component.scss'
})
export class CalendarMonthLineComponent implements AfterViewInit, OnDestroy {
  @Input() position: number = 1;
  @ViewChild('line') lineElement!: ElementRef;
  @ViewChild('container') containerElement!: ElementRef;
  @Output() positionChange = new EventEmitter<number>();

  calculatedPosition: number = -16;
  isDragging: boolean = false;
  startX: number = 0;
  startPosition: number = 0;
  private resizeObserver: ResizeObserver | null = null;

  ngAfterViewInit() {
    setTimeout(() => {
      this.centerOnPosition();
      this.setupResizeObserver();
    });
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver() {
    if (!this.containerElement?.nativeElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.centerOnPosition();
    });

    this.resizeObserver.observe(this.containerElement.nativeElement);
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.centerOnPosition();
  }

  onTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.startX = event.touches[0].clientX;
    this.startPosition = this.calculatedPosition;
    event.preventDefault();
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;

    const currentX = event.touches[0].clientX;
    const deltaX = currentX - this.startX;
    const newPosition = this.startPosition + deltaX;

    const containerWidth = this.containerElement?.nativeElement?.clientWidth || 0;
    const margin = 16;
    this.calculatedPosition = Math.max(-margin, Math.min(newPosition, containerWidth - 32 + margin));

    event.preventDefault();
    this.updatePosition();
  }

  onTouchEnd(event: TouchEvent) {
    this.isDragging = false;
    event.preventDefault();
    this.centerOnPosition();
  }

  updatePosition() {
    const lineElement = this.lineElement?.nativeElement;
    const containerElement = this.containerElement?.nativeElement;
    if (!lineElement || !containerElement) return;

    const lineRect = lineElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    const relativePosition = lineRect.left - containerRect.left;

    const segments = 12;
    const segmentSize = containerRect.width / segments;
    const clampedPosition = Math.max(0, Math.min(relativePosition, containerRect.width - 0.0001));
    const newPosition = Math.floor(clampedPosition / segmentSize) + 1;
    if (this.position !== newPosition) {
      this.position = newPosition;
      this.positionChange.emit(this.position);
    }
  }

  centerOnPosition() {
    const containerElement = this.containerElement?.nativeElement;
    if (!containerElement) return;

    if (containerElement.clientWidth === 0) {
      setTimeout(() => this.centerOnPosition(), 100);
      return;
    }

    const containerWidth = containerElement.clientWidth;
    const segmentWidth = containerWidth / 12;
    const linePosition = this.position * segmentWidth - segmentWidth / 2;
    this.calculatedPosition = linePosition - 25;
  }
}
